// Locates and patches the rate cells baked into a generated estimating
// template's "RATES USED" block, by their exact column-A label text — never
// a fixed address, since the two current templates put them on different
// rows. Mirrors templates/estimating/build_unit_type_floor_plan_a.py and
// build_custom_line_item.py's find_one() calls exactly: both generators
// bake in these same named catalog entries. Never touches ExcelImporter.js.
import JSZip from "jszip";
import * as XLSX from "xlsx/xlsx.mjs";
// Explicit ".js" so the same module resolves under Node's ESM loader (the
// tools/verify-*.mjs tests) and under webpack.
import { findLabelValueAddr, findSubtotalAddr, findQuantityField, readSubtotalFromResults } from "./jobMath.js";

const OOXML_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const OOXML_REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

function findOne(items, name) {
  const item = (items || []).find((it) => it.name === name);
  if (!item) throw new Error(`Rate catalog is missing a "${name}" entry required by the estimating templates.`);
  return item;
}

// kind "number": the catalog value must be a finite number > 0 (or >= 0
// when allowZero), else resolveRateCellValue throws. Fix round 1: a
// missing, null or empty value used to be written into the cell as TEXT
// ("undefined", "null", "") and every later gate passed anyway. Markup
// allows 0 because a 0% markup is a legitimate business choice; a zero
// price, coverage or coat count is not. kind "text": always written as a
// string (inlineStr), so a name like "200 Series Eggshell" never becomes
// the number 200.
const PAINT = "Sherwin ProMar 200 Eggshell";
const RATE_CELL_LABELS = [
  { label: "Paint product:", kind: "text", category: "paint_product", entry: PAINT, key: "name" },
  { label: "Paint cost/gallon:", kind: "number", category: "paint_product", entry: PAINT, key: "cost_per_gallon" },
  { label: "Paint coverage (sq ft/gallon):", kind: "number", category: "paint_product", entry: PAINT, key: "coverage_sqft_per_gallon" },
  { label: "Coats:", kind: "number", category: "paint_product", entry: PAINT, key: "default_coats" },
  { label: "Paint labor rate ($/hr):", kind: "number", category: "labor_rate", entry: "Paint Labor", key: "rate" },
  { label: "Prep labor rate ($/hr):", kind: "number", category: "labor_rate", entry: "Prep Labor", key: "rate" },
  { label: "Light prep rate ($/linear ft):", kind: "number", category: "prep_severity_tier", entry: "Light", key: "rate" },
  { label: "Moderate prep rate ($/linear ft):", kind: "number", category: "prep_severity_tier", entry: "Moderate", key: "rate" },
  { label: "Heavy prep rate ($/linear ft):", kind: "number", category: "prep_severity_tier", entry: "Heavy", key: "rate" },
  { label: "Markup (%):", kind: "number", category: "markup", entry: "Standard", key: "percent", allowZero: true },
];

function describeValue(v) {
  return v === undefined ? "undefined" : JSON.stringify(v);
}

function resolveRateCellValue(spec, catalog) {
  const item = findOne(catalog?.[spec.category], spec.entry);
  const v = item[spec.key];
  const invalid = () => new Error(`Rate catalog "${spec.entry}" has no valid ${spec.key} (got ${describeValue(v)}).`);
  if (spec.kind === "number") {
    if (typeof v !== "number" || !Number.isFinite(v)) throw invalid();
    if (spec.allowZero ? v < 0 : v <= 0) throw invalid();
    return { kind: "number", value: v };
  }
  if (v === undefined || v === null || String(v).trim() === "") throw invalid();
  return { kind: "text", value: String(v) };
}

// Ruling R15: labor PRODUCTIVITY is not in a value cell. Both generators
// bake it into a formula constant (B28 "=B18/150") and into the label text
// beside it (A28 "Paint labor hours (est. 150 sq ft/hr):", written with
// Python's "{:g}" format). This patcher never rewrites formulas, so it
// cannot carry a productivity change into a stored template. Instead it
// reads the number back out of the label and, if the catalog now says
// something different, fails that template so the all-or-nothing rule
// blocks the whole upload.
const PRODUCTIVITY_CHECKS = [
  {
    kind: "paint",
    unit: "sq ft/hr",
    labelName: "paint labor hours",
    labelPattern: "Paint labor hours (est. N sq ft/hr):",
    re: /^Paint labor hours \(est\. (\S+) sq ft\/hr\):$/,
    entry: "Paint Labor",
    key: "productivity_sqft_per_hour",
  },
  {
    kind: "prep",
    unit: "linear ft/hr",
    labelName: "prep labor hours",
    labelPattern: "Prep labor hours (est. N linear ft/hr):",
    re: /^Prep labor hours \(est\. (\S+) linear ft\/hr\):$/,
    entry: "Prep Labor",
    key: "productivity_lf_per_hour",
  },
];

const REGENERATE_HINT = "The Rates screen can update prices, but a productivity change needs the unit type templates regenerated.";

function findLabelMatches(ws, re) {
  const found = [];
  if (!ws || !ws["!ref"]) return found;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (!cell || typeof cell.v !== "string") continue;
      const m = cell.v.trim().match(re);
      if (m) found.push(m[1]);
    }
  }
  return found;
}

// Pure: reads the productivity numbers baked into a worksheet's labor-hours
// label text and compares them with the catalog's Paint Labor / Prep Labor
// entries. Returns null when they all match, otherwise a message naming the
// problem. A missing or duplicated label, or a catalog entry without a
// productivity number, is a failure too: an estimating template whose baked
// productivity can't be confirmed must never be reported as up to date.
//
// Comparison is at 6 significant digits, because that is all the label
// carries ("{:g}"): a catalog value of 150.1234567 bakes into the label as
// "150.123", and must still count as matching.
export function checkBakedLaborProductivity(ws, catalog) {
  const changes = [];
  for (const chk of PRODUCTIVITY_CHECKS) {
    const found = findLabelMatches(ws, chk.re);
    if (found.length === 0) {
      return `Could not find the ${chk.labelName} label ("${chk.labelPattern}") in this template, so its baked-in labor productivity can't be checked. The unit type templates need to be regenerated.`;
    }
    if (found.length > 1) {
      return `Found ${found.length} ${chk.labelName} labels in this template; expected exactly one. The unit type templates need to be regenerated.`;
    }
    const baked = Number(found[0]);
    if (!Number.isFinite(baked)) {
      return `Could not read the ${chk.kind} productivity from the label "${found[0]}". The unit type templates need to be regenerated.`;
    }
    let entry;
    try {
      entry = findOne(catalog?.labor_rate, chk.entry);
    } catch (err) {
      return err.message;
    }
    const current = entry[chk.key];
    if (typeof current !== "number" || !Number.isFinite(current)) {
      return `Rate catalog's "${chk.entry}" entry has no ${chk.key} number, so the labor productivity baked into the unit type templates can't be checked.`;
    }
    if (Number(current.toPrecision(6)) !== baked) {
      changes.push(`${chk.kind} ${baked} -> ${current} ${chk.unit}`);
    }
  }
  if (changes.length === 0) return null;
  return `Labor productivity changed (${changes.join(", ")}). ${REGENERATE_HINT}`;
}

// Detects whether a worksheet is one of the estimating templates this
// patcher actually knows how to update: it must have exactly one subtotal
// row (findSubtotalAddr throws otherwise) AND carry at least one of the
// known rate labels. A template that fails either check — an unrelated
// ASTM template, say — is not a candidate at all. The caller (App.js's
// updateUnitTypes) uses this to SKIP such a template silently, never to
// fail it; a template this function never claims to understand was never
// promised an update in the first place, so it can't block one.
export function isEstimatingTemplate(ws) {
  try {
    findSubtotalAddr(ws);
  } catch {
    return false;
  }
  return RATE_CELL_LABELS.some(({ label }) => {
    try { findLabelValueAddr(ws, label); return true; } catch { return false; }
  });
}

async function resolveSheetPath(zip, sheetName) {
  const wbXml = await zip.file("xl/workbook.xml").async("string");
  const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
  const sheets = wbDoc.getElementsByTagNameNS(OOXML_NS, "sheet");
  let rId = null;
  for (const s of sheets) {
    if (s.getAttribute("name") === sheetName) {
      rId = s.getAttributeNS(OOXML_REL_NS, "id") || s.getAttribute("r:id");
      break;
    }
  }
  if (!rId) return null;
  const relsXml = await zip.file("xl/_rels/workbook.xml.rels").async("string");
  const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");
  for (const rel of relsDoc.getElementsByTagName("Relationship")) {
    if (rel.getAttribute("Id") === rId) {
      const target = rel.getAttribute("Target");
      return target.startsWith("/") ? target.slice(1) : `xl/${target}`;
    }
  }
  return null;
}
// Overwrites only the existing <v>/<is> of a cell that is already there —
// never creates a new cell, never touches <f>, styles, or anything else. A
// missing target cell is a genuine label/address mismatch and throws rather
// than fabricating a cell out of position. Numbers are written as a plain
// <v> (no t attribute, i.e. numeric); text is written as t="inlineStr"
// with <is><t>...</t></is>, which is what the generators write.
function patchOneCellInSheetXml(sheetXml, addr, { kind, value }) {
  const doc = new DOMParser().parseFromString(sheetXml, "application/xml");
  if (doc.querySelector("parsererror")) throw new Error("Sheet XML failed to parse.");
  let target = null;
  for (const c of doc.getElementsByTagNameNS(OOXML_NS, "c")) {
    if (c.getAttribute("r") === addr) { target = c; break; }
  }
  if (!target) throw new Error(`Cell ${addr} does not exist in this sheet. Refusing to fabricate it.`);
  for (const tag of ["v", "is"]) {
    for (const el of Array.from(target.getElementsByTagNameNS(OOXML_NS, tag))) target.removeChild(el);
  }
  // CT_Cell child order is f, v, is, extLst: with v/is removed, the new
  // child goes before extLst if there is one, otherwise at the end.
  const extLst = Array.from(target.childNodes).find((n) => n.localName === "extLst") || null;
  if (kind === "number") {
    target.removeAttribute("t");
    const v = doc.createElementNS(OOXML_NS, "v");
    v.textContent = String(value);
    target.insertBefore(v, extLst);
  } else {
    const text = String(value);
    target.setAttribute("t", "inlineStr");
    const is = doc.createElementNS(OOXML_NS, "is");
    const t = doc.createElementNS(OOXML_NS, "t");
    if (/^\s|\s$/.test(text)) t.setAttributeNS("http://www.w3.org/XML/1998/namespace", "xml:space", "preserve");
    t.textContent = text;
    is.appendChild(t);
    target.insertBefore(is, extLst);
  }
  return new XMLSerializer().serializeToString(doc);
}

// Children of <workbook> that the schema places AFTER calcPr. A missing
// calcPr is inserted before the first of these, never appended past them.
const WORKBOOK_AFTER_CALCPR = new Set([
  "oleSize", "customWorkbookViews", "pivotCaches", "smartTagPr", "smartTagTypes",
  "webPublishing", "fileRecoveryPr", "webPublishObjects", "extLst",
]);

// Drops the (now stale) calc chain completely: the part itself, its
// relationship in xl/_rels/workbook.xml.rels and its Override in
// [Content_Types].xml. Leaving the latter two pointing at a missing part is
// what makes Excel offer to "repair" the file. Then forces a full recalc
// on open.
async function dropCalcChainAndForceRecalc(zip) {
  zip.remove("xl/calcChain.xml");

  const relsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (relsFile) {
    const relsDoc = new DOMParser().parseFromString(await relsFile.async("string"), "application/xml");
    const stale = Array.from(relsDoc.getElementsByTagName("Relationship")).filter((rel) =>
      (rel.getAttribute("Type") || "").endsWith("/calcChain") || /(^|\/)calcChain\.xml$/.test(rel.getAttribute("Target") || ""));
    if (stale.length) {
      for (const rel of stale) rel.parentNode.removeChild(rel);
      zip.file("xl/_rels/workbook.xml.rels", new XMLSerializer().serializeToString(relsDoc));
    }
  }

  const ctFile = zip.file("[Content_Types].xml");
  if (ctFile) {
    const ctDoc = new DOMParser().parseFromString(await ctFile.async("string"), "application/xml");
    const stale = Array.from(ctDoc.getElementsByTagName("Override")).filter((o) =>
      (o.getAttribute("PartName") || "").toLowerCase() === "/xl/calcchain.xml");
    if (stale.length) {
      for (const o of stale) o.parentNode.removeChild(o);
      zip.file("[Content_Types].xml", new XMLSerializer().serializeToString(ctDoc));
    }
  }

  const wbDoc = new DOMParser().parseFromString(await zip.file("xl/workbook.xml").async("string"), "application/xml");
  let calcPr = wbDoc.getElementsByTagNameNS(OOXML_NS, "calcPr")[0];
  if (!calcPr) {
    const workbookEl = wbDoc.getElementsByTagNameNS(OOXML_NS, "workbook")[0];
    calcPr = wbDoc.createElementNS(OOXML_NS, "calcPr");
    const before = Array.from(workbookEl.childNodes).find((n) => n.nodeType === 1 && WORKBOOK_AFTER_CALCPR.has(n.localName)) || null;
    workbookEl.insertBefore(calcPr, before);
  }
  calcPr.setAttribute("fullCalcOnLoad", "1");
  zip.file("xl/workbook.xml", new XMLSerializer().serializeToString(wbDoc));
}

function resolveSheetKey(wb, templateSheetName) {
  return wb.Sheets[templateSheetName] ? templateSheetName : wb.SheetNames[0];
}

// Low-level writer: puts the given { label: { kind, value } } values into
// the cells beside those labels, with NO catalog validation. Every label
// given must exist exactly once. patchRateCellsInWorkbook is the only
// production caller and validates first; this is exported so tests can
// build deliberately broken templates to prove the later gates catch them.
export async function writeRateCellsUnchecked(rawBytes, templateSheetName, valuesByLabel) {
  const wb = XLSX.read(rawBytes, { type: "array", cellStyles: true });
  const sheetKey = resolveSheetKey(wb, templateSheetName);
  const ws = wb.Sheets[sheetKey];

  const zip = await JSZip.loadAsync(rawBytes);
  const sheetPath = await resolveSheetPath(zip, sheetKey);
  if (!sheetPath || !zip.file(sheetPath)) throw new Error(`Could not locate sheet "${sheetKey}" inside the workbook.`);

  let sheetXml = await zip.file(sheetPath).async("string");
  for (const [label, cellValue] of Object.entries(valuesByLabel)) {
    sheetXml = patchOneCellInSheetXml(sheetXml, findLabelValueAddr(ws, label), cellValue);
  }
  zip.file(sheetPath, sheetXml);
  await dropCalcChainAndForceRecalc(zip);

  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE", compressionOptions: { level: 6 } });
}

// Patches every rate cell this template has a label for and returns the
// repacked workbook's bytes. A label absent from a given template is
// skipped (not every template need carry every label); a label present
// more than once throws (jobMath.findLabelValueAddr's duplicate check),
// exactly like the subtotal lookup — never silently pick one. A labor
// productivity change (R15, see checkBakedLaborProductivity) or an invalid
// catalog value (resolveRateCellValue) throws before anything is patched.
export async function patchRateCellsInWorkbook(rawBytes, templateSheetName, catalog) {
  const wb = XLSX.read(rawBytes, { type: "array", cellStyles: true });
  const sheetKey = resolveSheetKey(wb, templateSheetName);
  const ws = wb.Sheets[sheetKey];

  const productivityProblem = checkBakedLaborProductivity(ws, catalog);
  if (productivityProblem) throw new Error(productivityProblem);

  const valuesByLabel = {};
  for (const spec of RATE_CELL_LABELS) {
    try {
      findLabelValueAddr(ws, spec.label);
    } catch (err) {
      if (err.message.startsWith("No cell labelled")) continue; // not on this template — fine
      throw err; // duplicate label — never skip that
    }
    valuesByLabel[spec.label] = resolveRateCellValue(spec, catalog);
  }
  if (Object.keys(valuesByLabel).length === 0) throw new Error(`No known rate labels were found in "${sheetKey}", so nothing was patched.`);

  return writeRateCellsUnchecked(rawBytes, sheetKey, valuesByLabel);
}

// Fix round 1, Important 2: the pre-upload sanity evaluation must use a
// fixed, non-zero report. With an empty report every cost term is 0 x rate,
// so the subtotal is 0 whatever the rates are and a broken rate cell sails
// through. Every text/number input gets "100"; the "Number of units..."
// quantity field (findQuantityField), where the template has one, gets "1".
export const SAMPLE_INPUT_VALUE = "100";
export function buildSampleReport(template) {
  const sections = template?.isMultiSheet
    ? (template.sheets || []).flatMap((sh) => sh.sections || [])
    : (template?.sections || []);
  const qty = findQuantityField(template);
  const report = {};
  for (const sec of sections) {
    for (const f of sec.fields || []) {
      if (f.type !== "text" && f.type !== "number") continue;
      report[f.id] = qty && f.id === qty.id ? "1" : SAMPLE_INPUT_VALUE;
    }
  }
  return report;
}

// The post-patch gate. First, every numeric rate cell present on the
// patched sheet must actually hold a number in range: the sample subtotal
// alone can't prove that, because the evaluator treats a text cell as 0 and
// the other cost terms keep the subtotal positive (a single "undefined" in
// the cost/gallon cell still gives a positive subtotal). Then reads the
// subtotal out of a sample evaluation (buildSampleReport) and requires it
// to be a finite number > 0. Throws on either, which fails the template
// and so blocks the all-or-nothing upload.
export function requirePositiveSampleSubtotal(ws, evalResults) {
  for (const spec of RATE_CELL_LABELS) {
    if (spec.kind !== "number") continue;
    let addr;
    try { addr = findLabelValueAddr(ws, spec.label); } catch { continue; } // absent here; duplicates were rejected at patch time
    const cell = ws[addr];
    const ok = cell && cell.t === "n" && Number.isFinite(cell.v) && (spec.allowZero ? cell.v >= 0 : cell.v > 0);
    if (!ok) {
      throw new Error(`Rate cell ${addr} ("${spec.label}") does not hold a valid number after patching (got ${describeValue(cell ? cell.v : undefined)}).`);
    }
  }
  const subtotal = readSubtotalFromResults(ws, evalResults);
  if (!Number.isFinite(subtotal) || subtotal <= 0) {
    throw new Error(`Sample check: with ${SAMPLE_INPUT_VALUE} in every input the subtotal came out ${subtotal}; expected a positive number. A rate cell is probably zero or broken.`);
  }
  return subtotal;
}

// Final fix wave, item 3: the catalog shape this module reads
// ({ paint_product: [{ name, ...data }], ... }) from the rows
// getRateCatalogItemsOrThrow returns. Shared by "Update unit types" and the
// pre-send staleness check, so both always read the catalog the same way.
export function buildRateCatalog(items) {
  const catalog = { paint_product: [], labor_rate: [], prep_severity_tier: [], markup: [] };
  for (const it of items || []) {
    if (it && it.category in catalog) catalog[it.category].push({ name: it.name, ...(it.data || {}) });
  }
  return catalog;
}

// Final fix wave, item 3 (ruling R28): a dry run of the patch. For every
// rate label this sheet carries (RATE_CELL_LABELS, the same list the patcher
// writes), compares the catalog's current value (resolveRateCellValue, the
// same resolution) with the value baked into the cell beside the label, and
// adds the baked labor productivity check (checkBakedLaborProductivity).
// Writes nothing. Returns one message per difference; an empty list means
// this template already carries exactly the catalog's rates. A catalog
// entry that is missing or invalid, or a duplicated label, is a difference
// too: the template cannot be shown to be current.
export function findStaleRateCells(ws, catalog) {
  const differences = [];
  for (const spec of RATE_CELL_LABELS) {
    let addr;
    try {
      addr = findLabelValueAddr(ws, spec.label);
    } catch (err) {
      if (err.message.startsWith("No cell labelled")) continue; // not on this template
      differences.push(`${spec.label} ${err.message}`);
      continue;
    }
    let expected;
    try {
      expected = resolveRateCellValue(spec, catalog);
    } catch (err) {
      differences.push(`${spec.label} ${err.message}`);
      continue;
    }
    const cell = ws[addr];
    const baked = cell ? cell.v : undefined;
    const same = expected.kind === "number"
      ? cell?.t === "n" && baked === expected.value
      : baked !== undefined && baked !== null && String(baked) === expected.value;
    if (!same) differences.push(`${spec.label} the template has ${describeValue(baked)}, the catalog has ${describeValue(expected.value)}.`);
  }
  if (RATE_CELL_LABELS.some(({ label }) => { try { findLabelValueAddr(ws, label); return true; } catch { return false; } })) {
    const productivity = checkBakedLaborProductivity(ws, catalog);
    if (productivity) differences.push(productivity);
  }
  return differences;
}

export const RATES_STALE_MESSAGE = "Rates have changed since the unit types were last updated. Click Update Unit Types (or reload if another user already did) before sending.";

// Final fix wave, item 3 (ruling R28): the check Mark Sent and the draft
// preview run first. All I/O is injected: loadCatalogItems() resolves the
// catalog rows (App.js: getRateCatalogItemsOrThrow) and each template's
// loadBytes() resolves its stored xlsx (App.js: getRawBytesForTemplate).
// Only estimating templates (isEstimatingTemplate) are compared; anything
// else has no rates to go stale. Resolves { ok: true } or { ok: false,
// reason, message, differences? } with reason "catalog" (the catalog could
// not be loaded), "template" (a stored template could not be read) or
// "stale" (a baked rate differs from the catalog). Fails closed: nothing it
// could not confirm counts as current.
export async function checkRatesCurrent({ loadCatalogItems, templates }) {
  let catalog;
  try {
    catalog = buildRateCatalog(await loadCatalogItems());
  } catch (err) {
    return {
      ok: false,
      reason: "catalog",
      message: `Could not load the rate catalog, so there is no way to confirm the unit types use the current rates. Nothing was done.${err?.message ? ` (${err.message})` : ""}`,
    };
  }
  const differences = [];
  for (const t of templates || []) {
    const unreadable = (why) => ({
      ok: false,
      reason: "template",
      message: `Could not read the stored template "${t.name}", so there is no way to confirm it uses the current rates. Nothing was done.${why ? ` (${why})` : ""}`,
    });
    let bytes;
    try {
      bytes = await t.loadBytes();
    } catch (err) {
      return unreadable(err?.message);
    }
    if (!bytes) return unreadable(null);
    let ws;
    try {
      const wb = XLSX.read(bytes, { type: "array" });
      ws = wb.Sheets[resolveSheetKey(wb, t.name)];
    } catch (err) {
      return unreadable(err?.message);
    }
    if (!isEstimatingTemplate(ws)) continue;
    for (const d of findStaleRateCells(ws, catalog)) differences.push(`${t.name}: ${d}`);
  }
  if (differences.length > 0) return { ok: false, reason: "stale", message: RATES_STALE_MESSAGE, differences };
  return { ok: true };
}

// All-or-nothing upload planner: given the per-template patch/re-import/eval
// results, decides whether ANY template should be uploaded. One failure
// blocks every upload, and names exactly which template(s) failed (spec
// section 5: "Update unit types fails on any template: nothing is
// uploaded, and the failing template is named").
export function planUnitTypeUpdate(results) {
  const failing = results.filter((r) => !r.ok);
  return {
    uploadAll: failing.length === 0,
    failing: failing.map((r) => ({ templateId: r.templateId, templateName: r.templateName, error: r.error })),
  };
}
